import { type Err } from './common';
import { type Bundle } from './types';

/*
 * Combat HUD overhaul (Wave 0 contract): a port of shared-cs ContentValidator.CombatHud.cs with the same messages in the same order.
 * The server runs it right after the throwables. Keys: raidRules sprintLeanMin / sprintRampDelayS / sprintRampS / hordeWarnAt and the
 * optional blocks autoCombat, camera and hud (absent or null = the C# defaults); squadRules.remoteHealM; squadRules.ai's team-disposition
 * and hunt & kill keys; items[].weapon.attackLabel.
 */

/** Mirrors `ContentValidator.MaxAttackLabelLength`. */
export const MAX_ATTACK_LABEL_LENGTH = 8;

/** shared-cs `CombatDispositionIds.Player` (the YOU ring). */
export const PLAYER_DISPOSITIONS = ['hot', 'aim', 'hold'] as const;
/** shared-cs `CombatDispositionIds.TeamStart`: the team dispositions a raid may start in (never kill). */
export const TEAM_START_DISPOSITIONS = ['clearHot', 'silent', 'hold'] as const;

/** shared-cs `AutoCombatDef` (`raidRules.autoCombat`). */
export type AutoCombatDef = {
  acquireS?: number;
  retargetHysteresis?: number;
  targetRangeMult?: number;
  onScreenOnly?: boolean;
  steadyAutoMult?: number;
  defaultDisposition?: string;
};
/** shared-cs `RaidCameraDef` (`raidRules.camera`). */
export type RaidCameraDef = {
  minViewWM?: number;
  minViewHM?: number;
  leadEaseS?: number;
  leadMaxMps?: number;
  leadHoldS?: number;
  nearViewWM?: number;
  nearViewHM?: number;
  nearDistance?: number;
  farViewWM?: number;
  farViewHM?: number;
  farDistance?: number;
};
/** shared-cs `RaidHudDef` (`raidRules.hud`). */
export type RaidHudDef = { hubDwellS?: number };

/** The new flat `raidRules` fields and blocks. */
export type CombatHudRaidFields = {
  sprintLeanMin?: number;
  sprintRampDelayS?: number;
  sprintRampS?: number;
  hordeWarnAt?: number;
  autoCombat?: AutoCombatDef | null;
  camera?: RaidCameraDef | null;
  hud?: RaidHudDef | null;
};

/** The new `squadRules.ai` fields. */
export type CombatHudSquadAiFields = {
  defaultTeamDisposition?: string;
  holdAvoidM?: number;
  silentTakedownMaxM?: number;
  huntSweptSeenFrac?: number;
  huntForceLockedDoors?: boolean;
  huntLiftsKillCap?: boolean;
  huntHomeInOnLast?: boolean;
  huntRepathS?: number;
  huntGiveUpS?: number;
  huntAreaClearToastS?: number;
};

export const RAID_SPRINT_DEFAULTS = {
  sprintLeanMin: 0.95,
  sprintRampDelayS: 0.4,
  sprintRampS: 0.3,
  hordeWarnAt: 0.75,
} as const;
export const AUTO_COMBAT_DEFAULTS = {
  acquireS: 0.25,
  retargetHysteresis: 0.25,
  targetRangeMult: 0.9,
  onScreenOnly: true,
  steadyAutoMult: 0,
  defaultDisposition: 'hot',
} as const;
export const RAID_CAMERA_DEFAULTS = {
  minViewWM: 16,
  minViewHM: 14.1,
  leadEaseS: 0.7,
  leadMaxMps: 2.5,
  leadHoldS: 0.5,
  nearViewWM: 8,
  nearViewHM: 7,
  nearDistance: 40,
  farViewWM: 10.7,
  farViewHM: 9.5,
  farDistance: 20,
} as const;
export const RAID_HUD_DEFAULTS = { hubDwellS: 0.25 } as const;
export const SQUAD_REMOTE_HEAL_DEFAULT = 8;
export const SQUAD_AI_COMBAT_HUD_DEFAULTS = {
  defaultTeamDisposition: 'clearHot',
  holdAvoidM: 4,
  silentTakedownMaxM: 8,
  huntSweptSeenFrac: 0.8,
  huntForceLockedDoors: true,
  huntLiftsKillCap: true,
  huntHomeInOnLast: true,
  huntRepathS: 1,
  huntGiveUpS: 30,
  huntAreaClearToastS: 2.5,
} as const;

const inRange = (v: number, min: number, max: number) => v >= min && v <= max;

export function validateCombatHud(b: Bundle, err: Err) {
  (b.items ?? []).forEach((it, i) => {
    const label = (it?.weapon as { attackLabel?: string | null } | null | undefined)?.attackLabel;
    if (label != null && (label.trim().length === 0 || label.length > MAX_ATTACK_LABEL_LENGTH))
      err(
        'items',
        i,
        'weapon.attackLabel',
        `Weapon '${it?.id ?? ''}' needs attackLabel of 1..${MAX_ATTACK_LABEL_LENGTH} characters (or none).`,
      );
  });

  const raid = b.raidRules as (CombatHudRaidFields & object) | null | undefined;
  if (raid) {
    const r = { ...RAID_SPRINT_DEFAULTS, ...stripUndefined(raid) };
    const range = (
      path: string,
      v: number,
      min: number,
      max: number,
      where = 'Raid rules need',
    ) => {
      if (!inRange(v, min, max))
        err('raidRules', undefined, path, `${where} ${path.split('.').pop()} in ${min}..${max}.`);
    };
    range('sprintLeanMin', r.sprintLeanMin, 0.5, 1);
    range('sprintRampDelayS', r.sprintRampDelayS, 0, 2);
    range('sprintRampS', r.sprintRampS, 0, 2);
    if (!(r.hordeWarnAt === 0 || (r.hordeWarnAt > 0 && r.hordeWarnAt < 1)))
      err(
        'raidRules',
        undefined,
        'hordeWarnAt',
        'Raid rules need hordeWarnAt 0 (off) or above 0 and below 1.',
      );

    if (raid.autoCombat) {
      const ac = { ...AUTO_COMBAT_DEFAULTS, ...stripUndefined(raid.autoCombat) };
      const w = 'Raid rules autoCombat needs';
      range('autoCombat.acquireS', ac.acquireS, 0, 2, w);
      range('autoCombat.retargetHysteresis', ac.retargetHysteresis, 0, 1, w);
      if (!(ac.targetRangeMult > 0 && ac.targetRangeMult <= 1.5))
        err(
          'raidRules',
          undefined,
          'autoCombat.targetRangeMult',
          `${w} targetRangeMult above 0 and at most 1.5.`,
        );
      range('autoCombat.steadyAutoMult', ac.steadyAutoMult, 0, 1, w);
      if (!(PLAYER_DISPOSITIONS as readonly string[]).includes(ac.defaultDisposition ?? ''))
        err(
          'raidRules',
          undefined,
          'autoCombat.defaultDisposition',
          `${w} defaultDisposition hot, aim or hold.`,
        );
    }
    if (raid.camera) {
      const c = { ...RAID_CAMERA_DEFAULTS, ...stripUndefined(raid.camera) };
      range('camera.minViewWM', c.minViewWM, 4, 60, 'Raid rules camera needs');
      range('camera.minViewHM', c.minViewHM, 4, 60, 'Raid rules camera needs');
      range('camera.nearViewWM', c.nearViewWM, 4, 60, 'Raid rules camera needs');
      range('camera.nearViewHM', c.nearViewHM, 4, 60, 'Raid rules camera needs');
      range('camera.nearDistance', c.nearDistance, 10, 80, 'Raid rules camera needs');
      range('camera.farViewWM', c.farViewWM, 4, 60, 'Raid rules camera needs');
      range('camera.farViewHM', c.farViewHM, 4, 60, 'Raid rules camera needs');
      range('camera.farDistance', c.farDistance, 10, 80, 'Raid rules camera needs');
      range('camera.leadEaseS', c.leadEaseS, 0.05, 3, 'Raid rules camera needs');
      range('camera.leadMaxMps', c.leadMaxMps, 0.5, 20, 'Raid rules camera needs');
      range('camera.leadHoldS', c.leadHoldS, 0, 3, 'Raid rules camera needs');
    }
    if (raid.hud) {
      const h = { ...RAID_HUD_DEFAULTS, ...stripUndefined(raid.hud) };
      range('hud.hubDwellS', h.hubDwellS, 0.05, 1, 'Raid rules hud needs');
    }
  }

  const sq = b.squadRules as
    | ({ remoteHealM?: number; reviveRangeM?: number; ai?: CombatHudSquadAiFields | null } & object)
    | null
    | undefined;
  if (!sq) return;
  const heal = sq.remoteHealM ?? SQUAD_REMOTE_HEAL_DEFAULT;
  if (!(heal >= (sq.reviveRangeM ?? 1.2) && heal <= 30))
    err(
      'squadRules',
      undefined,
      'remoteHealM',
      'Squad rules need remoteHealM from reviveRangeM up to 30.',
    );
  if (sq.ai == null) return; // the squad rules check reports the missing block
  const ai = { ...SQUAD_AI_COMBAT_HUD_DEFAULTS, ...stripUndefined(sq.ai) };
  const range = (k: keyof CombatHudSquadAiFields & string, v: number, min: number, max: number) => {
    if (!inRange(v, min, max))
      err('squadRules', undefined, `ai.${k}`, `Squad AI needs ${k} in ${min}..${max}.`);
  };
  if (!(TEAM_START_DISPOSITIONS as readonly string[]).includes(ai.defaultTeamDisposition ?? ''))
    err(
      'squadRules',
      undefined,
      'ai.defaultTeamDisposition',
      'Squad AI needs defaultTeamDisposition clearHot, silent or hold.',
    );
  range('holdAvoidM', ai.holdAvoidM, 0, 15);
  range('silentTakedownMaxM', ai.silentTakedownMaxM, 0, 30);
  if (!(ai.huntSweptSeenFrac > 0 && ai.huntSweptSeenFrac <= 1))
    err(
      'squadRules',
      undefined,
      'ai.huntSweptSeenFrac',
      'Squad AI needs huntSweptSeenFrac above 0 and at most 1.',
    );
  range('huntRepathS', ai.huntRepathS, 0.1, 10);
  range('huntGiveUpS', ai.huntGiveUpS, 5, 300);
  range('huntAreaClearToastS', ai.huntAreaClearToastS, 0.5, 10);
}

/** Drops undefined and null fields, so a spread over the defaults keeps the default for an absent key (as the C# deserializer does). */
function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined && v !== null),
  ) as Partial<T>;
}
